import { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUp, ArrowDown, ChevronsUp, Menu } from 'react-feather';
import Popover from './Popover';

const sinTildes = (t) => String(t).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Mostrar, ocultar y reordenar columnas. Las fijas (Acciones) no aparecen.
 * Arriba, las que están en la tabla (con su orden); debajo, las disponibles
 * agrupadas por tema y filtrables con el buscador.
 */
const SelectorColumnas = ({ ancla = null, tabla, onClose }) => {
  const [busqueda, setBusqueda] = useState('');
  const [arrastrando, setArrastrando] = useState(null);
  const editables = tabla.orden.filter((id) => !tabla.columnas.find((c) => c.id === id).fixed);
  const visibles = editables.filter((id) => !tabla.ocultas.has(id));
  const q = sinTildes(busqueda.trim());
  const coincide = (col) => !q || sinTildes(`${col.label} ${col.group || ''}`).includes(q);

  const mover = (id, delta) => {
    const i = editables.indexOf(id);
    // Se mueve entre columnas visibles: saltar las ocultas haría parecer que no pasa nada.
    const vecina = visibles[visibles.indexOf(id) + delta];
    if (i < 0 || !vecina) return;
    const j = editables.indexOf(vecina);
    const nuevo = [...editables];
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    tabla.fijarOrden(nuevo);
  };

  // Coloca `id` justo antes de `antesDe` (o al principio de las visibles si es null).
  const reordenado = (id, antesDe) => {
    const resto = editables.filter((x) => x !== id);
    const destino = antesDe ?? visibles.find((x) => x !== id);
    const pos = destino ? resto.indexOf(destino) : resto.length;
    return [...resto.slice(0, pos), id, ...resto.slice(pos)];
  };

  const alPrincipio = (id) => {
    if (tabla.ocultas.has(id)) tabla.mostrarConOrden(id, reordenado(id, null));
    else tabla.fijarOrden(reordenado(id, null));
  };

  const soltar = (destinoId) => {
    if (arrastrando && arrastrando !== destinoId)
      tabla.fijarOrden(reordenado(arrastrando, destinoId));
    setArrastrando(null);
  };

  const colDe = (id) => tabla.columnas.find((c) => c.id === id);

  const disponibles = editables.map(colDe).filter((c) => tabla.ocultas.has(c.id) && coincide(c));
  const grupos = disponibles.reduce((acc, col) => {
    const g = col.group || 'Otras';
    (acc[g] = acc[g] || []).push(col);
    return acc;
  }, {});

  return (
    <Popover ancla={ancla} onClose={onClose} ancho={320} alinear="derecha" etiqueta="Columnas">
      <div className="tbl-pop-titulo">
        Columnas ({visibles.length} de {editables.length})
      </div>
      <input
        type="search"
        className="tbl-input tbl-cols-buscar"
        placeholder="Buscar columna…"
        aria-label="Buscar columna"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
      />
      <ul className="tbl-cols">
        <li className="tbl-cols-seccion">En la tabla</li>
        {visibles
          .map(colDe)
          .filter(coincide)
          .map((col, i, lista) => (
            <li
              key={col.id}
              className={`tbl-col-item${arrastrando === col.id ? ' tbl-arrastrando' : ''}`}
              draggable={!q}
              onDragStart={() => setArrastrando(col.id)}
              onDragOver={(e) => arrastrando && e.preventDefault()}
              onDrop={() => soltar(col.id)}
              onDragEnd={() => setArrastrando(null)}
            >
              {!q && <Menu size={13} className="tbl-asa" aria-hidden="true" />}
              <label className="tbl-opcion">
                <input type="checkbox" checked onChange={() => tabla.alternarColumna(col.id)} />
                <span className="tbl-opcion-txt">{col.label}</span>
              </label>
              {!q && (
                <>
                  <button
                    type="button"
                    className="tbl-icono"
                    aria-label={`Al principio ${col.label}`}
                    title="Al principio"
                    disabled={i === 0}
                    onClick={() => alPrincipio(col.id)}
                  >
                    <ChevronsUp size={13} />
                  </button>
                  <button
                    type="button"
                    className="tbl-icono"
                    aria-label={`Subir ${col.label}`}
                    disabled={i === 0}
                    onClick={() => mover(col.id, -1)}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    className="tbl-icono"
                    aria-label={`Bajar ${col.label}`}
                    disabled={i === lista.length - 1}
                    onClick={() => mover(col.id, 1)}
                  >
                    <ArrowDown size={13} />
                  </button>
                </>
              )}
            </li>
          ))}
        {Object.entries(grupos).map(([grupo, cols]) => (
          <li key={grupo}>
            <div className="tbl-cols-grupo">{grupo}</div>
            <ul className="tbl-cols tbl-cols-anidada">
              {cols.map((col) => (
                <li key={col.id} className="tbl-col-item">
                  <label className="tbl-opcion">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => tabla.alternarColumna(col.id)}
                    />
                    <span className="tbl-opcion-txt">{col.label}</span>
                  </label>
                  <button
                    type="button"
                    className="tbl-icono"
                    aria-label={`Mostrar ${col.label} al principio`}
                    title="Mostrar al principio"
                    onClick={() => alPrincipio(col.id)}
                  >
                    <ChevronsUp size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
        {q && visibles.map(colDe).filter(coincide).length === 0 && disponibles.length === 0 && (
          <li className="tbl-vacio-txt">Ninguna columna coincide</li>
        )}
      </ul>
      <div className="tbl-pop-pie">
        <button type="button" className="tbl-btn" onClick={tabla.restablecerColumnas}>
          Restablecer
        </button>
        <button type="button" className="tbl-btn tbl-btn-primario" onClick={onClose}>
          Listo
        </button>
      </div>
    </Popover>
  );
};

SelectorColumnas.propTypes = {
  ancla: PropTypes.object,
  tabla: PropTypes.shape({
    orden: PropTypes.array.isRequired,
    columnas: PropTypes.array.isRequired,
    ocultas: PropTypes.instanceOf(Set).isRequired,
    alternarColumna: PropTypes.func.isRequired,
    mostrarConOrden: PropTypes.func.isRequired,
    fijarOrden: PropTypes.func.isRequired,
    restablecerColumnas: PropTypes.func.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SelectorColumnas;
